import axios from 'axios';

import type { EmployeeOnboardingBlocker, PayrollBlocker } from './generated/v1/model';

/** The v1 error envelope: every non-2xx is `{ "error": { ... } }`. */
interface PayrollErrorEnvelope {
    error: {
        code: string;
        message: string;
        resolution: string | null;
        retryable: boolean;
        /** Per-code extras: a refusal's `reason` and `blockers`, an in-flight 409's `command_uuid`. */
        details?: Record<string, unknown>;
    };
}

/**
 * The copy for a failure that carried no v1 envelope to quote — a transport error, or a body the
 * backend did not write. Exported so callers that have to report a failure *before* any request
 * exists (a submit handler with nothing to send) say the same thing as one that did.
 */
export const PAYROLL_GENERIC_ERROR = 'Something went wrong. Please try again.';

/** The member's payroll provider connection has expired; only reconnecting can fix it. */
export const PROVIDER_NOT_CONNECTED = 'PROVIDER_NOT_CONNECTED';

/** The command is still out there: the provider may yet apply it, so the client must not re-send. */
const COMMAND_IN_FLIGHT = 'COMMAND_IN_FLIGHT';

/**
 * The confirm refusals a caller recovers from by code. The generated envelope types `code` as a
 * plain string, so these are named once here rather than spelt out at each comparison.
 */
export const PayrollErrorCodes = {
    /** A state still asks the new hire report question and the body did not answer it. */
    NEW_HIRE_REPORT_UNANSWERED: 'NEW_HIRE_REPORT_UNANSWERED',
    /** The body's new hire report answer differs from one already on file, which is final. */
    NEW_HIRE_REPORT_ALREADY_SET: 'NEW_HIRE_REPORT_ALREADY_SET',
    /** The employee still has onboarding steps of their own to finish; nothing was sent. */
    WORKER_ONBOARDING_INCOMPLETE: 'WORKER_ONBOARDING_INCOMPLETE',
    /** A move outside the business's states that the member has not confirmed; nothing was recorded. */
    OUT_OF_STATE_UNCONFIRMED: 'OUT_OF_STATE_UNCONFIRMED',
} as const;

/**
 * Codes the client must treat as terminal even though the envelope marks them retryable. A
 * command stranded in `sent` may already have been applied at the provider, and the sweeper that
 * would resolve it does not exist yet (AP-2156) — retrying is how one intent becomes two.
 */
const NEVER_RETRY = new Set([COMMAND_IN_FLIGHT, 'COMMAND_ALREADY_FAILED', 'IDEMPOTENCY_KEY_REUSED']);

/**
 * `axios.isAxiosError`, never `instanceof AxiosError`. The app resolves two copies of axios, so
 * the error `collectiveApi` throws fails an `instanceof` check while reporting
 * `constructor.name === 'AxiosError'` — which made every reader below silently fall through to
 * the generic path for real errors, while hand-built `new AxiosError(...)` fixtures passed.
 */
const responseOf = (error: unknown) => (axios.isAxiosError(error) ? error.response : undefined);

/**
 * A 404 the endpoint documents as an answer rather than a failure — `GET /compensation/` returns
 * one for a member who has not set a salary ("You haven't set a salary yet"), which is a state to
 * render, not an error to report. Callers map it to `null` data so the two stay distinguishable.
 *
 * Reads the response for the same reason `hasSettledAsFailure` does: `collectiveApi`'s errors do
 * not survive `instanceof`, so a check on the error's class answers "not a 404" for every real one.
 */
export const isPayrollNotFound = (error: unknown): boolean => responseOf(error)?.status === 404;

const envelopeOf = (error: unknown): PayrollErrorEnvelope['error'] | null => {
    const data = responseOf(error)?.data;

    if (data && typeof data === 'object' && 'error' in data) {
        const inner = (data as PayrollErrorEnvelope).error;

        if (inner && typeof inner.code === 'string' && typeof inner.message === 'string') {
            return inner;
        }
    }

    return null;
};

export const payrollErrorCode = (error: unknown): string | null => envelopeOf(error)?.code ?? null;

/**
 * Error responses an endpoint documents as an answer, which its caller renders as a state. Matched on
 * the body's error code as well as the status, so any other failure on the same endpoint still reaches RUM.
 */
const EXPECTED_ERROR_RESPONSES = [
    { status: 404, code: 'COMPENSATION_NOT_SET' },
    // No employee is known to be the member yet; payroll asks them which one is.
    { status: 404, code: 'OWNER_NOT_FOUND' },
    // A dead Gusto grant: the member has to reconnect, and Overview asks them to.
    { status: 422, code: PROVIDER_NOT_CONNECTED },
    // A run or contractor payment with no receipt, mostly $0 runs; the receipt shows why.
    { status: 404, code: 'RECEIPT_UNAVAILABLE' },
] as const;

export const isExpectedPayrollError = (error: unknown): boolean => {
    const status = responseOf(error)?.status;
    const code = payrollErrorCode(error);

    return EXPECTED_ERROR_RESPONSES.some((expected) => expected.status === status && expected.code === code);
};

/**
 * The states a confirm was refused for, when the refusal is `NEW_HIRE_REPORT_UNANSWERED`, and
 * `null` for any other failure. Gusto will not finish an employee's onboarding while that
 * employer-only question is open, and it only lists the question once the employee reaches admin
 * review — so this refusal can be the first the page hears of it.
 *
 * An empty array is still a refusal: a `details` that names no states must reveal the question,
 * not hide it behind an unrecognised shape.
 */
export const unansweredNewHireReportStates = (error: unknown): string[] | null => {
    const inner = envelopeOf(error);

    if (inner?.code !== PayrollErrorCodes.NEW_HIRE_REPORT_UNANSWERED) {
        return null;
    }

    const states = inner.details?.states;

    return Array.isArray(states) ? states.filter((state): state is string => typeof state === 'string') : [];
};

/**
 * The steps a confirm was refused for, when the refusal is `WORKER_ONBOARDING_INCOMPLETE`, and
 * `null` for any other failure. The same list the onboarding-blockers read returns, so a page whose
 * read is stale can show it without waiting on a second request.
 */
export const onboardingBlockersOf = (error: unknown): EmployeeOnboardingBlocker[] | null => {
    const inner = envelopeOf(error);

    if (inner?.code !== PayrollErrorCodes.WORKER_ONBOARDING_INCOMPLETE) {
        return null;
    }

    const blockers = inner.details?.blockers;

    return Array.isArray(blockers)
        ? blockers.filter(
              (blocker): blocker is EmployeeOnboardingBlocker =>
                  typeof blocker?.key === 'string' && typeof blocker?.message === 'string'
          )
        : [];
};

/** `hourly_rate` → `Hourly rate`. A serializer's field names match the labels the forms use. */
const fieldLabel = (field: string): string => {
    const words = field.replaceAll('_', ' ');

    return words.charAt(0).toUpperCase() + words.slice(1);
};

/**
 * A 400's `details.fields` as one line per field, e.g. `Hourly rate: A valid number is required.`
 * The envelope's own message for that 400 is only "One or more fields are invalid.", which leaves
 * the member guessing which. `null` when there is nothing readable to name, so the caller keeps it.
 */
const invalidFieldsMessage = (details: Record<string, unknown> | undefined): string | null => {
    const fields = details?.fields;

    if (!fields || typeof fields !== 'object') {
        return null;
    }

    const lines = Object.entries(fields).flatMap(([field, messages]) => {
        const texts = (Array.isArray(messages) ? messages : [messages]).filter(
            (message): message is string => typeof message === 'string'
        );

        if (texts.length === 0) {
            return [];
        }

        return field === 'non_field_errors' ? texts : [`${fieldLabel(field)}: ${texts.join(' ')}`];
    });

    return lines.length > 0 ? lines.join(' ') : null;
};

/**
 * The backend writes member-safe `message` strings deliberately ("This worker changed while you
 * were editing. Reload and try again."), so this surfaces them rather than mapping codes to copy
 * of our own — two sources of wording drift apart. A field validation failure names the fields
 * instead (see `invalidFieldsMessage`). The fallback covers a non-envelope body only.
 */
export const payrollErrorMessage = (error: unknown): string => {
    const inner = envelopeOf(error);

    if (!inner) {
        return PAYROLL_GENERIC_ERROR;
    }

    return invalidFieldsMessage(inner.details) ?? inner.message;
};

export const isRetryable = (error: unknown): boolean => {
    const inner = envelopeOf(error);

    if (!inner) {
        return false;
    }

    return inner.retryable && !NEVER_RETRY.has(inner.code);
};

/**
 * Whether the server has told us this attempt is over and nothing is still running.
 *
 * This is the distinction the idempotency key turns on, and the two halves fail in opposite
 * directions. A command whose outcome we cannot know — no response at all, a timeout, a 5xx, or
 * an explicit `COMMAND_IN_FLIGHT` — may still be applied at the provider, so the next attempt has
 * to carry the *same* key and replay one intent; a fresh key there is how one termination becomes
 * two. A 4xx carrying the v1 envelope is the opposite: the request was refused, so either the
 * ledger never wrote a row or it wrote a FAILED one, and `_reject_settled_failure` refuses that
 * key from then on — a member correcting the date and pressing the button again gets a 409 they
 * can only escape by closing the dialog.
 *
 * Judged on the response rather than the error's class, deliberately. `collectiveApi`'s errors do
 * not survive `instanceof` (see `responseOf`), so a check on the class reports "unknown" for
 * every real refusal and the key never rotates.
 */
export const hasSettledAsFailure = (error: unknown): boolean => {
    const response = responseOf(error);

    // No response: the request never got an answer, so the provider's state is unknown.
    if (!response) {
        return false;
    }

    // 5xx may have been applied before it broke; only a refusal is proof nothing ran.
    if (response.status < 400 || response.status >= 500) {
        return false;
    }

    // A 4xx with no envelope is not proof of anything: `collectiveApiMutator` passes the body
    // through untouched, so a gateway or auth rejection that never reached the ledger arrives
    // looking much like one that did. The two errors are not symmetric — keeping a spent key costs
    // a 409 the member escapes by reopening the dialog, while retiring a live one is how a
    // termination gets recorded twice — so this answers "settled" only when the server named a
    // code for it.
    const code = payrollErrorCode(error);

    return code !== null && code !== COMMAND_IN_FLIGHT;
};
/**
 * Why a command was refused. A closed set per endpoint: submit and skip answer the first four, skip
 * also answers `period_not_on_schedule`, cancel answers `deadline_passed`, off-cycle — which names no run — answers the next four, and
 * the AutoPayroll enable answers `auto_payroll_blocked`, and removing a duplicate-period payroll
 * answers `already_processed`, `no_duplicate`, `provider_refused` and `unavailable`.
 */
export type RefusalReason =
    | 'deadline_passed'
    | 'payroll_blocked'
    | 'already_processed'
    | 'wrong_status'
    | 'payroll_not_connected'
    | 'unknown_pay_schedule'
    | 'empty_roster'
    | 'unknown_workers'
    | 'auto_payroll_blocked'
    | 'submission_blocker_unanswered'
    | 'unblock_option_not_offered'
    | 'duplicate_blocker_selection'
    | 'no_duplicate'
    | 'provider_refused'
    | 'unavailable'
    | 'recalculation_required'
    | 'calculation_in_flight'
    | 'period_not_on_schedule';

const REFUSAL_REASONS: RefusalReason[] = [
    'deadline_passed',
    'payroll_blocked',
    'already_processed',
    'wrong_status',
    'payroll_not_connected',
    'unknown_pay_schedule',
    'empty_roster',
    'unknown_workers',
    'auto_payroll_blocked',
    'submission_blocker_unanswered',
    'unblock_option_not_offered',
    'duplicate_blocker_selection',
    'no_duplicate',
    'provider_refused',
    'unavailable',
    'recalculation_required',
    'calculation_in_flight',
    'period_not_on_schedule',
];

/**
 * An unrecognised reason is dropped rather than carried through. Every branch downstream compares
 * it against a known value, so an unknown one can only ever mean "not that one" — and typing it as
 * a `RefusalReason` anyway hides a contract change behind a screen that quietly renders the generic
 * banner instead.
 */
const asRefusalReason = (value: unknown): RefusalReason | null =>
    REFUSAL_REASONS.find((reason) => reason === value) ?? null;

/**
 * What a blocker arriving through `details` is, as opposed to one on the run payload.
 *
 * The run's own blockers come through the generated client and are whole `PayrollBlocker`s. These
 * come out of `Record<string, unknown>`, so the type is narrowed to the two fields the envelope is
 * checked for and the refusal banner reads — the message it prints and the code it deduplicates on.
 * Promising `action_url` and `severity` as well would be a claim nothing verified.
 */
export type RefusalBlocker = Pick<PayrollBlocker, 'code' | 'message'>;

const isBlocker = (value: unknown): value is RefusalBlocker =>
    typeof value === 'object' &&
    value !== null &&
    typeof (value as RefusalBlocker).code === 'string' &&
    typeof (value as RefusalBlocker).message === 'string';

/** One way past a submission blocker, as the provider offers it. `metadata` differs per option. */
export interface UnblockOption {
    unblock_type: string;
    check_date: string | null;
    metadata: Record<string, unknown>;
}

/**
 * A submission blocker a submit refusal asks the member to answer, e.g. a run over the fast-ACH
 * limit with `wire_in` and `move_to_four_day`. A different shape from `RefusalBlocker`: it is keyed
 * by `blocker_type` and carries the options to choose from.
 */
export interface SubmissionBlocker {
    blocker_type: string;
    blocker_name: string;
    unblock_options: UnblockOption[];
}

const isUnblockOption = (value: unknown): value is UnblockOption =>
    typeof value === 'object' && value !== null && typeof (value as UnblockOption).unblock_type === 'string';

/** Checked rather than cast, for the reason `readPayrollError` gives. Options that fail the check are dropped. */
const asSubmissionBlocker = (value: unknown): SubmissionBlocker | null => {
    if (typeof value !== 'object' || value === null) {
        return null;
    }
    const {
        blocker_type: blockerType,
        blocker_name: blockerName,
        unblock_options: options,
    } = value as Record<string, unknown>;
    if (typeof blockerType !== 'string' || !Array.isArray(options)) {
        return null;
    }

    return {
        blocker_type: blockerType,
        blocker_name: typeof blockerName === 'string' ? blockerName : '',
        unblock_options: options.filter(isUnblockOption).map((option) => ({
            unblock_type: option.unblock_type,
            check_date: typeof option.check_date === 'string' ? option.check_date : null,
            metadata: typeof option.metadata === 'object' && option.metadata !== null ? option.metadata : {},
        })),
    };
};

export interface PayrollErrorInfo {
    code: string;
    message: string;
    /** `self_serve`, `wait`, or `contact_support`. */
    resolution: string | null;
    /** The envelope's own flag. Prefer `isRetryable()`, which also subtracts `NEVER_RETRY`. */
    retryable: boolean;
    /** Present on `COMMAND_REFUSED`; the discriminator every refusal branch reads. */
    reason: RefusalReason | null;
    /** Present on a `payroll_blocked` refusal. Narrower than the run's own blockers — see `RefusalBlocker`. */
    blockers: RefusalBlocker[];
    /** Present on a `submission_blocker_unanswered` or `unblock_option_not_offered` refusal: what to choose from. */
    submissionBlockers: SubmissionBlocker[];
    /** Present on `COMMAND_IN_FLIGHT` — the row to poll instead of retrying. */
    commandUuid: string | null;
    /**
     * Present on a `recalculation_required` refusal when the backend started the recalculation —
     * the calculate command to wait on before the member can submit again.
     */
    recalculationCommandUuid: string | null;
    /**
     * The provider's own words for a refusal, passed through by the backend on `details.provider_messages`.
     * Present on an `auto_payroll_blocked` refusal. Empty when the provider said nothing quotable.
     */
    providerMessages: string[];
    /**
     * Present on a prepare or calculate `PAYROLL_NOT_FOUND` when the provider deleted the run and one run
     * took its place in the same pay period — switching AutoPayroll replaces a schedule's upcoming runs
     * this way.
     */
    replacedBy: string | null;
    /** A published-but-unbuilt endpoint. Waiting is the only recovery; retrying is not. */
    isNotImplemented: boolean;
}

/**
 * A failure that is not the payroll envelope — a dropped connection, a proxy error, a 5xx. Never a
 * refusal, so `reason` and `blockers` stay empty and `retryable` is true: nothing here says the
 * provider declined, only that we did not hear back.
 */
export const genericPayrollError = (error: unknown): PayrollErrorInfo => ({
    code: 'UNKNOWN',
    message: payrollErrorMessage(error),
    resolution: null,
    retryable: true,
    reason: null,
    blockers: [],
    submissionBlockers: [],
    commandUuid: null,
    recalculationCommandUuid: null,
    providerMessages: [],
    replacedBy: null,
    isNotImplemented: false,
});

/**
 * What a settled command reports when it failed at the provider. The command carries its own code
 * and message, so this is not a refusal either — the request was accepted and then did not work.
 */
export const settledCommandError = (command: { error_code: string; error_message: string }): PayrollErrorInfo => ({
    code: command.error_code,
    message: command.error_message || payrollErrorMessage(null),
    resolution: null,
    retryable: false,
    reason: null,
    blockers: [],
    submissionBlockers: [],
    commandUuid: null,
    recalculationCommandUuid: null,
    providerMessages: [],
    replacedBy: null,
    isNotImplemented: false,
});

/**
 * The whole envelope, for callers that must branch on *why* rather than just show a message.
 *
 * `details` is `Record<string, unknown>`, so everything read out of it is checked rather than cast.
 * A blockers array carrying a null reaches the deduplication in `ReviewAndSubmitSection`, which
 * reads `blocker.code`, and takes the refusal screen down with it — the member is then told nothing
 * at all about a submission that failed.
 *
 * `payrollErrorMessage` above stays the right call for "show the member what happened" — the
 * backend writes member-safe strings deliberately. This is for the submit flow, which has to route
 * a refusal to its own screen. Returns `null` for anything that is not the payroll envelope, which
 * is the caller's signal to fall back to a generic failure: a network drop is not a refusal and must
 * not be rendered as one.
 */
export const readPayrollError = (error: unknown): PayrollErrorInfo | null => {
    const payload = envelopeOf(error);

    if (!payload) {
        return null;
    }

    const details = payload.details ?? {};

    return {
        code: payload.code,
        message: payload.message,
        resolution: payload.resolution,
        retryable: payload.retryable,
        reason: asRefusalReason(details.reason),
        blockers: Array.isArray(details.blockers) ? details.blockers.filter(isBlocker) : [],
        submissionBlockers: Array.isArray(details.blockers)
            ? details.blockers
                  .map(asSubmissionBlocker)
                  .filter((blocker): blocker is SubmissionBlocker => blocker !== null)
            : [],
        commandUuid: typeof details.command_uuid === 'string' ? details.command_uuid : null,
        recalculationCommandUuid:
            typeof details.recalculation_command_uuid === 'string' ? details.recalculation_command_uuid : null,
        providerMessages: Array.isArray(details.provider_messages)
            ? details.provider_messages.filter((message): message is string => typeof message === 'string')
            : [],
        replacedBy: typeof details.replaced_by === 'string' ? details.replaced_by : null,
        isNotImplemented: payload.code === 'NOT_IMPLEMENTED',
    };
};
